import React, {useCallback, useContext, useEffect, useReducer, useState} from 'react';
import {BaseStyle} from "../../../../../styles/shared/BaseStyle";
import {useParams} from "react-router-dom";
import {
	Backdrop, Checkbox,
	CircularProgress,
	Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Paper,
	Typography
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import {useForm, FormProvider} from "react-hook-form";
import {
	initQuaranteneState,
	QUARANTENE_INITIAL_STATE,
	QuaranteneReducer
} from "../../../../../reducers/QuaranteneReducer";
import {calcPunteggioIsolamento, MonitoraggioIsolamentoGrid} from "./MonitoraggioIsolamentoGrid";
import {
	generateMonitoraggio,
} from "../../../../../services/QuaranteneServices";
import {getMonitoraggioApi} from "../../../../../services/utils/MonitoraggioApiData";
import {AppContext} from "../../../../../App";
import {setCloseSnackbar, setOpenSnackbar, setThemeType} from "../../../../../services/AppServices";
import {calcPunteggioQuarantenaBoolean, MonitoraggioQuarantenaGrid} from "./MonitoraggioQuarantenaGrid";
import clsx from "clsx";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from '@mui/material/Alert';
import Background from "../../../../../assets/Ocean-bg.jpg";
import {OCEAN__GLOBAL_THEMA_TEXT_DARK, OCEAN__MAIN_GLOBAL_THEMA_TEXT} from "../../../../../strings/IT";
import IconButton from "@mui/material/IconButton";
import Brightness4TwoToneIcon from "@mui/icons-material/Brightness4TwoTone";
import Brightness5TwoToneIcon from "@mui/icons-material/Brightness5TwoTone";
import {ContattaciStyle} from "../../../../../styles/pages/contact/ContattaciStyle";
import SimpleDialog from "../../../../widgets/dialogs/SimpleDialog";
import {useTheme} from "@mui/material/styles";

const FormMonitoraggio = () => {
	const classes = BaseStyle();
	const contattaciClasses = ContattaciStyle();
	let theme = useTheme()
	const appContext = useContext(AppContext);
	const [quarantenaState, quarantenaDispatch] = useReducer(QuaranteneReducer, QUARANTENE_INITIAL_STATE, initQuaranteneState);
	const methods = useForm({mode: "onChange"});
	let {id, token, idSoggetto, tipoQuarantena} = useParams();
	const [submit, setSubmit] = useState(false);
	const [openInformativa, setOpenInformativa] = useState(true);
	const [read, setRead] = useState(false);
	const [finish, setFinish] = useState(false);
	
	const handleCloseSnack = useCallback(() => {
		setCloseSnackbar(appContext.dispatch);
	}, [appContext.dispatch]);
	
	const onSubmit = useCallback((data) => {
		let allOk = true;
		if (data["febbre"] && (parseFloat(data["febbre"]) > 40 || parseFloat(data["febbre"]) < 33)) {
			allOk = false;
			setOpenSnackbar("warning", "Controlla di aver inserito dei valori corretti per la febbre!", appContext.dispatch);
		}
		if (data["saturazioneO2"] && (parseFloat(data["saturazioneO2"]) > 100 || parseFloat(data["saturazioneO2"]) < 80)) {
			allOk = false;
			setOpenSnackbar("warning", "Controlla di aver inserito dei valori corretti per la saturazione!", appContext.dispatch);
		}
		if (data["pressioneArteriosaMax"] && (parseFloat(data["pressioneArteriosaMax"]) > 180 || parseFloat(data["pressioneArteriosaMax"]) < 110)) {
			allOk = false;
			setOpenSnackbar("warning", "Controlla di aver inserito dei valori corretti per la pressione arteriosa massima!", appContext.dispatch);
		}
		if (allOk) {
			setSubmit(true);
			let score;
			if (tipoQuarantena === "iso") {
				score = calcPunteggioIsolamento(data);
			} else {
				let intermediateScore = calcPunteggioQuarantenaBoolean(data);
				score = intermediateScore * 4 / 12;
			}
			generateMonitoraggio(getMonitoraggioApi({
				...data,
				score: score
			}, id, idSoggetto, quarantenaState.quarantena?.tipo, token), quarantenaDispatch, token);
		}
	}, [id, quarantenaDispatch, appContext.dispatch, idSoggetto, quarantenaState.quarantena, token, tipoQuarantena])
	
	useEffect(() => {
		if (submit && !quarantenaState.loadingGenerateMonitoraggio && quarantenaState.errorGenerateMonitoraggio === "") {
			setSubmit(false);
			setFinish(true);
		} else if (submit && !quarantenaState.loadingGenerateMonitoraggio && quarantenaState.errorGenerateMonitoraggio !== "") {
			setSubmit(false);
			setOpenSnackbar("error", quarantenaState.errorGenerateMonitoraggio, appContext.dispatch);
		}
	}, [submit, appContext.dispatch, quarantenaState.loadingGenerateMonitoraggio, quarantenaState.errorGenerateMonitoraggio])
	
	useEffect(() => {
		document.body.style =
			`background-image: url(${Background});
		background-attachment: fixed;
    background-size: cover;`;
	}, []);
	
	return (
        <React.Fragment>
            <Backdrop open={submit} className={classes.backdrop}>
				<CircularProgress/>
			</Backdrop>
            <main className={classes.mainNoBgCol}>
				<header className={contattaciClasses.header}>
					<Tooltip
						title={appContext.state.theme.palette.mode === "light" ? OCEAN__MAIN_GLOBAL_THEMA_TEXT : OCEAN__GLOBAL_THEMA_TEXT_DARK}>
						<IconButton
                            className={contattaciClasses.icon}
                            color="inherit"
                            style={{color: appContext.state.theme.palette.mode === "light" ? "#FFF" : "#000"}}
                            onClick={() => setThemeType(appContext.state.theme.palette.mode === "light" ? "dark" : "light",
								appContext.state.theme, appContext.dispatch)}
                            size="large">
							{
								appContext.state.theme.palette.mode === "light" ?
									<Brightness4TwoToneIcon/> :
									<Brightness5TwoToneIcon/>
							}
						</IconButton>
					</Tooltip>
					<Typography variant={"h2"} component={"h1"} className={contattaciClasses.title}>
						<b>Monitoraggio sintomi</b>
					</Typography>
				</header>
				<article className={contattaciClasses.article}>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Paper
								className={clsx(classes.roundBorder, contattaciClasses.mdWidth, classes.transparent80, classes.blurred20)}>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										{
											tipoQuarantena === "iso" ?
												<MonitoraggioIsolamentoGrid paziente={true} read={read} setRead={setRead}/> :
												<MonitoraggioQuarantenaGrid paziente={true} read={read} setRead={setRead}/>
										}
									</Grid>
									<Grid item xs={12} style={{display: "flex", justifyContent: "center"}}>
										<Tooltip
											title={"Conferma i dati indicati nel form"}>
											<Button variant={"contained"} disableElevation color="primary" disabled={submit || !read}
											        style={{borderRadius: 24}}
											        className={classes.roundButton} onClick={methods.handleSubmit(onSubmit)}>
												<b>Conferma ed invia</b>
											</Button>
										</Tooltip>
									</Grid>
								</Grid>
							</Paper>
						</form>
					</FormProvider>
				</article>
				<Snackbar open={appContext.state.open} autoHideDuration={3000} onClose={handleCloseSnack}>
					<Alert style={{borderRadius: 28}} onClose={handleCloseSnack}
					       severity={appContext.state.severity}>
						{appContext.state.message}
					</Alert>
				</Snackbar>
			</main>
            {
				finish && <Dialog open={finish} PaperProps={{
					className: clsx(classes.roundBorder)
				}}>
					<DialogTitle>
						<b>Monitoraggio compilato con successo! 👍</b>
					</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Hai completato il tuo monitoraggio quotidiano, resta a disposizione per eventuali chiamate dagli
							operatori dell' ASL qualora rilevassero nel tuo monitoraggio dei dati anomali.<br/>
							Ora puoi chiudere questa finestra o tab, riceverai un'ulteriore email nel caso il tuo monitoraggio
							continui.
						</DialogContentText>
					</DialogContent>
				</Dialog>
			}
            <SimpleDialog
				open={openInformativa}
				title={"Benvenuto su Atlantis!"}
				content={
					<DialogContentText component={"div"}>
						<b>Informativa e privacy</b><br/>
						
						Gentile Sig./Sig.ra,<br/>
						
						I dati personali che conferirà con la compilazione del presente modulo on line saranno trattati utilizzando
						modalità manuali, informatiche e telematiche, nel rispetto e nei limiti stabiliti dalla normativa
						vigente.<br/>
						<br/>
						DICHIARAZIONE DI PRESA VISIONE DELL’INFORMATIVA PRIVACY. Dichiaro di avere letto e preso conoscenza delle
						informazioni, rese ai sensi dell’art. 13 del Regolamento UE 2016/679, relative al trattamento dei miei dati
						personali, pubblicate alla pagina web {" "}
						<a style={{color: theme.palette.primary.main}}
						   href={"https://www.regione.piemonte.it/web/note-legali-privacy"}
						   target="_blank"
						   rel="noreferrer">https://www.regione.piemonte.it/web/note-legali-privacy</a>.<br/>
						<FormControlLabel
							control={
								<Checkbox
									color={"primary"}
									checked={read}
									onChange={() => setRead(!read)}
									name="read"/>
							}
							label={"Ho letto integralmente e compreso l'informativa sul trattamento dei dati personali"}
						/>
					</DialogContentText>
				}
				actions={
					<DialogActions>
						<Button variant={"contained"} disableElevation size={"small"} className={classes.roundButton}
						        color={"primary"} style={{borderRadius: 24}}
						        onClick={() => setOpenInformativa(false)} disabled={!read}>
							<b>Confermo</b>
						</Button>
					</DialogActions>
				}
			/>
        </React.Fragment>
    );
};

function Alert(props) {
	return <MuiAlert elevation={16} variant="filled" {...props} />;
}

export default React.memo(FormMonitoraggio);
//localhost:3000/monitoraggio/qua/50641/33362/wdnHehjT+Tbsif9lmavSgOyEra4b+gfIT8O9QHuAFnJkSTFci5UP8KGBqZDJRfSAR5BayiRC1M3nw79W0tXMzYMv5T2iTTCayWYgeNEgANsKQfXkn-gTDGF5ciqrUb-FP9cS+J-vf-YbjtEPMyTwfXt+AHhzWgFISMpKeYq0pP6y4dHZcAtiFKhQ45vdxiD03dRVdF5zBDGJUvtZXjZrwEJL+iohW09XqHUhimArUsQTLeFa2h63gaQWAXc-ErR8u8Lh2VUqVz895pjvVvoGLWv8vEwHV0abcl+wuAkXYVydaM34YVIYZPbKTHEFqJcdZjEH+9Sr9kFLyAOCe-p2l7NL4ZrLFcKvXQAWZLKsYeU=
